import {
	addDays,
	startOfDay,
	endOfDay,
	isBefore,
	isAfter,
	startOfWeek,
	endOfWeek,
	addYears,
	startOfQuarter,
	endOfQuarter,
	addQuarters,
} from "date-fns";
import { TZDate } from "@date-fns/tz";
import type { DateRange } from "react-day-picker";

export enum DateRangePreset {
	Today = "TODAY",
	ThisWeek = "THIS_WEEK",
	LastWeek = "LAST_WEEK",
	ThisMonth = "THIS_MONTH",
	LastMonth = "LAST_MONTH",
	ThisQuarter = "THIS_QUARTER",
	LastQuarter = "LAST_QUARTER",
	LastFYMar = "LAST_FY_MAR",
	LastFYJun = "LAST_FY_JUN",
	LastFYDec = "LAST_FY_DEC",
	ThisFYMar = "THIS_FY_MAR",
	ThisFYJun = "THIS_FY_JUN",
	ThisFYDec = "THIS_FY_DEC",
	Last7Days = "LAST_7_DAYS",
	Last30Days = "LAST_30_DAYS",
}

const DEFAULT_PRESET = [
	DateRangePreset.Today,
	DateRangePreset.ThisWeek,
	DateRangePreset.LastWeek,
	DateRangePreset.ThisMonth,
	DateRangePreset.LastMonth,
	DateRangePreset.LastFYMar,
	DateRangePreset.LastFYJun,
	DateRangePreset.LastFYDec,
	DateRangePreset.ThisFYMar,
	DateRangePreset.ThisFYJun,
	DateRangePreset.ThisFYDec,
	DateRangePreset.Last7Days,
	DateRangePreset.Last30Days,
];

export type DateRangePresetConfig = {
	label: string;
	value: string;
	getRange: () => DateRange;
	isValid: boolean;
};

export const getPresetRanges = ({
	minDate: min,
	maxDate: max,
	options = DEFAULT_PRESET,
	now,
	timeZone,
}: {
	minDate?: Date;
	maxDate?: Date;
	options?: DateRangePreset[];
	/**
	 * Reference "now" the presets are computed from. Defaults to the current instant. Pass a
	 * timezone-tagged value (e.g. `new TZDate(Date.now(), tz)`) together with `timeZone` to compute
	 * presets against a specific zone's calendar.
	 */
	now?: Date;
	/**
	 * IANA timezone the calendar-day boundaries are built in. When set, all constructed dates are
	 * `TZDate`s in this zone so month/quarter/FY boundaries land on the right calendar day; leave
	 * undefined for browser-timezone behaviour.
	 */
	timeZone?: string;
}): DateRangePresetConfig[] => {
	// Build calendar-day dates in the resolved zone (or browser-local when no `timeZone`), so
	// `startOfDay`/`endOfDay` and the month/year getters below all resolve on the right day.
	const makeDate = (year: number, month: number, day: number): Date =>
		timeZone
			? new TZDate(year, month, day, timeZone)
			: new Date(year, month, day);
	const defaultNow = timeZone ? new TZDate(Date.now(), timeZone) : new Date();

	const today = startOfDay(now ?? defaultNow);
	const yesterday = endOfDay(addDays(today, -1));
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();
	const minDate = min && startOfDay(min);
	const maxDate = max && endOfDay(max);

	const constrainDate = (date: Date): Date => {
		if (minDate && isBefore(date, minDate)) {
			return minDate;
		}
		if (maxDate && isAfter(date, maxDate)) {
			return maxDate;
		}
		return date;
	};

	const hasValidOverlap = (fromDate: Date, toDate: Date): boolean => {
		if (minDate && isAfter(minDate, toDate)) {
			return false;
		}
		if (maxDate && isBefore(maxDate, fromDate)) {
			return false;
		}
		return true;
	};

	// eslint-disable-next-line react-doctor/js-flatmap-filter -- The filter only drops the unreachable default branch; a flatMap would wrap every case.
	return options
		.map((preset) => {
			switch (preset) {
				case DateRangePreset.Today:
					return {
						label: "Today",
						value: "today",
						getRange: () => ({
							from: constrainDate(startOfDay(today)),
							to: constrainDate(endOfDay(today)),
						}),
						isValid: hasValidOverlap(startOfDay(today), endOfDay(today)),
					};
				case DateRangePreset.ThisWeek:
					return {
						label: "This Week",
						value: "this_week",
						getRange: () => ({
							from: constrainDate(startOfWeek(today)),
							to: constrainDate(endOfWeek(today)),
						}),
						isValid: hasValidOverlap(startOfWeek(today), endOfWeek(today)),
					};
				case DateRangePreset.LastWeek:
					return {
						label: "Last Week",
						value: "last_week",
						getRange: () => {
							const lastWeek = addDays(today, -7);
							return {
								from: constrainDate(startOfWeek(lastWeek)),
								to: constrainDate(endOfWeek(lastWeek)),
							};
						},
						isValid: hasValidOverlap(
							startOfWeek(addDays(today, -7)),
							endOfWeek(addDays(today, -7)),
						),
					};
				case DateRangePreset.ThisMonth:
					return {
						label: "This Month",
						value: "this_month",
						getRange: () => ({
							from: constrainDate(
								startOfDay(makeDate(currentYear, currentMonth, 1)),
							),
							to: constrainDate(
								endOfDay(makeDate(currentYear, currentMonth + 1, 0)),
							),
						}),
						isValid: hasValidOverlap(
							startOfDay(makeDate(currentYear, currentMonth, 1)),
							endOfDay(makeDate(currentYear, currentMonth + 1, 0)),
						),
					};
				case DateRangePreset.LastMonth:
					return {
						label: "Last Month",
						value: "last_month",
						getRange: () => ({
							from: constrainDate(
								startOfDay(makeDate(currentYear, currentMonth - 1, 1)),
							),
							to: constrainDate(
								endOfDay(makeDate(currentYear, currentMonth, 0)),
							),
						}),
						isValid: hasValidOverlap(
							startOfDay(makeDate(currentYear, currentMonth - 1, 1)),
							endOfDay(makeDate(currentYear, currentMonth, 0)),
						),
					};
				case DateRangePreset.ThisQuarter:
					return {
						label: "This Quarter",
						value: "this_quarter",
						getRange: () => ({
							from: constrainDate(startOfQuarter(today)),
							to: constrainDate(endOfQuarter(today)),
						}),
						isValid: hasValidOverlap(
							startOfQuarter(today),
							endOfQuarter(today),
						),
					};
				case DateRangePreset.LastQuarter:
					return {
						label: "Last Quarter",
						value: "last_quarter",
						getRange: () => {
							const lastQuarter = addQuarters(today, -1);
							return {
								from: constrainDate(startOfQuarter(lastQuarter)),
								to: constrainDate(endOfQuarter(lastQuarter)),
							};
						},
						isValid: hasValidOverlap(
							startOfQuarter(addQuarters(today, -1)),
							endOfQuarter(addQuarters(today, -1)),
						),
					};
				case DateRangePreset.LastFYMar: {
					// NZ financial year: Apr 1 - Mar 31
					const fyEnd = isBefore(today, startOfDay(makeDate(currentYear, 3, 1)))
						? endOfDay(makeDate(currentYear - 1, 2, 31))
						: endOfDay(makeDate(currentYear, 2, 31));
					const fyStart = startOfDay(makeDate(fyEnd.getFullYear() - 1, 3, 1));
					return {
						label: `Last FY (Mar ${fyEnd.getFullYear()})`,
						value: "last_fy_mar",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.LastFYJun: {
					// AU financial year: Jul 1 - Jun 30
					const fyEnd = isBefore(today, startOfDay(makeDate(currentYear, 6, 1)))
						? endOfDay(makeDate(currentYear - 1, 5, 30))
						: endOfDay(makeDate(currentYear, 5, 30));
					const fyStart = startOfDay(makeDate(fyEnd.getFullYear() - 1, 6, 1));
					return {
						label: `Last FY (Jun ${fyEnd.getFullYear()})`,
						value: "last_fy_jun",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.LastFYDec: {
					// Calendar year: Jan 1 - Dec 31
					const lastCalYear = addYears(today, -1);
					const fyStart = startOfDay(makeDate(lastCalYear.getFullYear(), 0, 1));
					const fyEnd = endOfDay(makeDate(lastCalYear.getFullYear(), 11, 31));
					return {
						label: `Last FY (Dec ${fyEnd.getFullYear()})`,
						value: "last_fy_dec",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.ThisFYMar: {
					// NZ financial year: Apr 1 – Mar 31
					const fyStart = isBefore(
						today,
						startOfDay(makeDate(currentYear, 3, 1)),
					)
						? startOfDay(makeDate(currentYear - 1, 3, 1))
						: startOfDay(makeDate(currentYear, 3, 1));
					const fyEnd = endOfDay(makeDate(fyStart.getFullYear() + 1, 2, 31));
					return {
						label: `This FY (Mar ${fyEnd.getFullYear()})`,
						value: "this_fy_mar",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.ThisFYJun: {
					// AU financial year: Jul 1 – Jun 30
					const fyStart = isBefore(
						today,
						startOfDay(makeDate(currentYear, 6, 1)),
					)
						? startOfDay(makeDate(currentYear - 1, 6, 1))
						: startOfDay(makeDate(currentYear, 6, 1));
					const fyEnd = endOfDay(makeDate(fyStart.getFullYear() + 1, 5, 30));
					return {
						label: `This FY (Jun ${fyEnd.getFullYear()})`,
						value: "this_fy_jun",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.ThisFYDec: {
					// Calendar year: Jan 1 – Dec 31
					const fyStart = startOfDay(makeDate(currentYear, 0, 1));
					const fyEnd = endOfDay(makeDate(currentYear, 11, 31));
					return {
						label: `This FY (Dec ${fyEnd.getFullYear()})`,
						value: "this_fy_dec",
						getRange: () => ({
							from: constrainDate(fyStart),
							to: constrainDate(fyEnd),
						}),
						isValid: hasValidOverlap(fyStart, fyEnd),
					};
				}
				case DateRangePreset.Last7Days:
					return {
						label: "Last 7 Days",
						value: "last7days",
						getRange: () => ({
							from: constrainDate(startOfDay(addDays(today, -6))),
							to: constrainDate(endOfDay(today)),
						}),
						isValid: hasValidOverlap(startOfDay(addDays(today, -6)), yesterday),
					};
				case DateRangePreset.Last30Days:
					return {
						label: "Last 30 Days",
						value: "last30days",
						getRange: () => ({
							from: constrainDate(startOfDay(addDays(today, -29))),
							to: constrainDate(endOfDay(today)),
						}),
						isValid: hasValidOverlap(
							startOfDay(addDays(today, -29)),
							yesterday,
						),
					};
				default: {
					preset satisfies never;
					return undefined;
				}
			}
		})
		.filter(Boolean) as DateRangePresetConfig[];
};
